import { handleApiError } from "./error";

const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY;

if (!ENCRYPTION_KEY) {
  throw new Error("VITE_ENCRYPTION_KEY environment variable is required");
}

try {
  const rawKey = atob(ENCRYPTION_KEY);
  if (rawKey.length !== 16 && rawKey.length !== 32) {
    throw new Error("Invalid encryption key length");
  }
} catch (error) {
  console.error("Encryption key validation failed - using fallback logging only", error);
}

export async function decryptData<T = unknown>(encryptedData: string): Promise<T> {
  try {
    const key = await getKey();
    const encryptedBytes = Uint8Array.from(atob(encryptedData), (c) => c.charCodeAt(0));

    const iv = encryptedBytes.slice(0, 12);
    const ciphertext = encryptedBytes.slice(12);

    const decryptedBuffer = await crypto.subtle.decrypt({ iv, name: "AES-GCM" }, key, ciphertext);

    const decryptedText = new TextDecoder().decode(decryptedBuffer);
    return JSON.parse(decryptedText) as T;
  } catch (error) {
    throw new Error(`Failed to decrypt data: ${handleApiError(error)}`);
  }
}

export async function encryptData(data: object): Promise<string> {
  const jsonData = JSON.stringify(data);
  const encodedData = new TextEncoder().encode(jsonData);
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 12-byte IV for GCM

  const encryptedBuffer = await crypto.subtle.encrypt({ iv, name: "AES-GCM" }, key, encodedData);

  // Combine IV and encrypted data
  const combinedArray = new Uint8Array(iv.length + encryptedBuffer.byteLength);
  combinedArray.set(iv, 0);
  combinedArray.set(new Uint8Array(encryptedBuffer), iv.length);

  return btoa(String.fromCharCode(...combinedArray)); // Base64 encode result
}

async function getKey(): Promise<CryptoKey> {
  try {
    // Ensure the decoded key is the correct length (32 bytes for AES-256)
    const rawKey = atob(ENCRYPTION_KEY);
    if (rawKey.length !== 16 && rawKey.length !== 32) {
      throw new Error("Encryption key must be 128 bits (16 bytes) or 256 bits (32 bytes)");
    }

    const keyData = new Uint8Array(rawKey.split("").map((char) => char.charCodeAt(0)));

    return await crypto.subtle.importKey(
      "raw",
      keyData,
      {
        length: keyData.length * 8,
        name: "AES-GCM",
      },
      false,
      ["encrypt", "decrypt"],
    );
  } catch (error) {
    console.error("Failed to create encryption key", error);
    throw new Error("Encryption key initialization failed");
  }
}
